/**
 * JWT Utility Functions
 * 
 * Provides functions to decode, validate, and extract claims from JWT tokens.
 * Works with GCIP/Firebase Auth tokens and mock tokens (development).
 * 
 * IMPORTANT: This utility does NOT verify signatures - that's the backend's job.
 * We only decode and extract claims for client-side use.
 * 
 * @module utils/jwt
 */

import { JWTClaims } from '@/types/auth';

/**
 * Decode JWT token and extract claims
 * 
 * @param token - JWT token string (format: header.payload.signature)
 * @returns Decoded claims or null if invalid
 * 
 * @example
 * const claims = decodeJWT(token);
 * if (claims) {
 *   console.log('User ID:', claims.ref_user_id);
 *   console.log('Expires:', new Date(claims.exp * 1000));
 * }
 */
export function decodeJWT(token: string): JWTClaims | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    
    if (parts.length !== 3) {
      console.error('[JWT] Invalid JWT format: expected 3 parts, got', parts.length);
      return null;
    }

    const [, payloadPart] = parts;
    
    if (!payloadPart) {
      console.error('[JWT] Invalid JWT: missing payload');
      return null;
    }

    // Decode base64url to base64
    let payload = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (payload.length % 4) {
      payload += '=';
    }

    // Decode and parse JSON
    const decodedPayload = atob(payload);
    const claims = JSON.parse(decodedPayload) as JWTClaims;

    return claims;
  } catch (error) {
    console.error('[JWT] Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 * 
 * @param token - JWT token string
 * @returns true if expired, false if valid
 */
export function isTokenExpired(token: string): boolean {
  const claims = decodeJWT(token);
  
  if (!claims || !claims.exp) {
    return true; // Treat invalid tokens as expired
  }

  const now = Math.floor(Date.now() / 1000);
  return claims.exp < now;
}

/**
 * Get ref_user_id from JWT token
 * 
 * @param token - JWT token string
 * @returns ref_user_id or null if not found
 */
export function getRefUserIdFromToken(token: string): string | null {
  const claims = decodeJWT(token);
  return claims?.ref_user_id || null;
}

/**
 * Get token expiry time in milliseconds
 * 
 * @param token - JWT token string
 * @returns Expiry timestamp in ms, or null if invalid
 */
export function getTokenExpiry(token: string): number | null {
  const claims = decodeJWT(token);
  return claims?.exp ? claims.exp * 1000 : null;
}

/**
 * Format token expiry as human-readable string
 * 
 * @param token - JWT token string
 * @returns Formatted expiry string or 'Invalid token'
 */
export function formatTokenExpiry(token: string): string {
  const expiry = getTokenExpiry(token);
  
  if (!expiry) {
    return 'Invalid token';
  }

  const expiryDate = new Date(expiry);
  const now = new Date();
  
  if (expiryDate < now) {
    return 'Expired';
  }

  const diffMs = expiry - now.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  
  if (diffHours > 0) {
    return `Expires in ${diffHours}h ${diffMins % 60}m`;
  } else {
    return `Expires in ${diffMins}m`;
  }
}

